import { ComponentProps } from 'react'

import {
  AvatarBadgeProps,
  Colors,
  type StatusLabelTypes,
} from '@factorialco/deprecated-design-system'
import { OneApprovalHistory } from '@factorialco/f0-react/dist/experimental'
import { ApprovalsMaterializedApprovalStepApproverApproverStatusEnum } from 'generated/resources/datatable.graphql'
import {
  ApprovalsApproverDetailsFragment,
  ApprovalsMaterializedApprovalFlowHistoryQuery,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { SELECTION_CRITERIA, roleInitials, roleName } from './roles'

export const REJECTED = 'rejected'
export const APPROVED = 'approved'
const PENDING = 'pending'
const IGNORED = 'ignored'
const CHANGES_REQUESTED = 'changes_requested'

type MaterializedStepType =
  ApprovalsMaterializedApprovalFlowHistoryQuery['approvals']['materializedApprovalsFlowsConnection']['nodes'][number]['steps'][number]

type StepType = NonNullable<
  ApprovalsMaterializedApprovalFlowHistoryQuery['approvals']['materializedApprovalsFlowsConnection']['nodes'][number]['approvalFlow']
>['steps'][number]

type NewStatus = ComponentProps<typeof OneApprovalHistory>['steps'][number]['status']

export interface ApproverProps {
  id: number
  status: string | null
  firstName: string
  lastName: string
  avatar?: string
  newStatus?: NewStatus
  accessId: number | null | undefined
  role: string | null
  roleInitials: string
  badge: AvatarBadgeProps
  updatedAt: string | null
  selectionCriteria?: string | null
}

interface stepStatusProps {
  label: string
  type: StatusLabelTypes
}

export const badgeColors = (
  isCurrentStep: boolean,
  status: string
): { color: Colors; textColor: Colors } => {
  return isCurrentStep
    ? { color: 'grey800', textColor: 'grey000White' }
    : status === PENDING || status === CHANGES_REQUESTED
      ? { color: 'grey200', textColor: 'grey600' }
      : { color: 'grey400', textColor: 'grey800' }
}

export const stepStatus = (
  status: string,
  isCurrentStatus: boolean,
  overrided: boolean
): stepStatusProps => {
  if (overrided && status !== APPROVED) {
    return {
      type: 'neutral',
      label: i18n.t('settings.approvals.approval_request_view.approval_history.ignored'),
    }
  }
  if (isCurrentStatus) {
    if (status === CHANGES_REQUESTED) {
      return {
        type: 'warning',
        label: i18n.t(
          'settings.approvals.approval_request_view.approval_history.changes_requested'
        ),
      }
    }

    return {
      type: 'warning',
      label: `${i18n.t(
        'settings.approvals.approval_request_view.approval_history.pending'
      )} - ${i18n.t('settings.approvals.approval_request_view.approval_history.current_step')}`,
    }
  }

  switch (status) {
    case APPROVED:
      return {
        type: 'positive',
        label: i18n.t('settings.approvals.approval_request_view.approval_history.approved'),
      }
    case PENDING:
      return {
        type: 'neutral',
        label: i18n.t('settings.approvals.approval_request_view.approval_history.waiting'),
      }
    case IGNORED:
      return {
        type: 'neutral',
        label: i18n.t('settings.approvals.approval_request_view.approval_history.ignored'),
      }
    case CHANGES_REQUESTED:
      return {
        type: 'warning',
        label: i18n.t(
          'settings.approvals.approval_request_view.approval_history.changes_requested'
        ),
      }
    case REJECTED:
      return {
        type: 'critical',
        label: i18n.t('settings.approvals.approval_request_view.approval_history.rejected'),
      }
    default:
      return {
        type: 'neutral',
        label: i18n.t('settings.approvals.approval_request_view.approval_history.waiting'),
      }
  }
}

const approverBadge = (status: string | null): AvatarBadgeProps => {
  switch (status) {
    case APPROVED:
      return {
        backgroundColor: 'viridian1200',
        color: 'grey000White',
        icon: 'check',
      }
    case PENDING:
      return {
        backgroundColor: 'grey600',
        color: 'grey000White',
        icon: 'question',
      }
    case IGNORED:
      return {
        backgroundColor: 'grey600',
        color: 'grey000White',
        icon: 'question',
      }
    case REJECTED:
      return {
        backgroundColor: 'radicalRed1200',
        color: 'grey000White',
        icon: 'cross',
      }
    case CHANGES_REQUESTED:
      return {
        backgroundColor: 'tangerine1000',
        color: 'grey000White',
        icon: 'alert',
      }
    default:
      return {
        backgroundColor: 'grey600',
        color: 'grey000White',
        icon: 'question',
      }
  }
}

export const approverTooltip = (
  fullName: string | null | undefined,
  selectionCriteria: string | null | undefined
) => {
  switch (selectionCriteria) {
    case SELECTION_CRITERIA.configuredapprover:
      return fullName
    case SELECTION_CRITERIA.delegateapprover:
      return i18n.t('settings.approvals.approval_request_view.approval_history.delegateapprover', {
        name: fullName,
      })
    case SELECTION_CRITERIA.managerasfallback:
    case SELECTION_CRITERIA.firstadminasfallback:
    case SELECTION_CRITERIA.adminasfallback:
    case SELECTION_CRITERIA.ownernoadmins:
      return i18n.t('settings.approvals.approval_request_view.approval_history.behalf_of', {
        name: fullName,
      })
    default:
      return fullName
  }
}

export const buildStepStatus = (approvers: ApproverProps[]) => {
  if (approvers.some((approver) => approver.status === 'approved')) {
    return 'approved'
  }
  if (approvers.some((approver) => approver.status === 'rejected')) {
    return 'rejected'
  }
  if (approvers.some((approver) => approver.status === CHANGES_REQUESTED)) {
    return CHANGES_REQUESTED
  }

  return 'pending'
}

export const buildNewStepStatus: (approvers: ApproverProps[]) => NewStatus = (approvers) => {
  if (approvers.some((approver) => approver.status === 'approved')) {
    return 'approved'
  }
  if (approvers.some((approver) => approver.status === 'rejected')) {
    return 'rejected'
  }
  if (approvers.some((approver) => approver.status === 'waiting')) {
    return 'waiting'
  }

  return 'pending'
}

const NEW_APPROVER_STATUS_MAP: Partial<
  Record<ApprovalsMaterializedApprovalStepApproverApproverStatusEnum, NewStatus>
> = {
  approved: 'approved',
  rejected: 'rejected',
  [CHANGES_REQUESTED]: 'waiting',
}

const buildNewApproverStatus: (
  status: ApprovalsMaterializedApprovalStepApproverApproverStatusEnum
) => NewStatus = (status) => {
  return NEW_APPROVER_STATUS_MAP[status] ?? 'pending'
}

const normalizeApproverInfo = (approverAccess?: ApprovalsApproverDetailsFragment | null) => {
  return {
    firstName: (approverAccess?.employee?.preferredName || approverAccess?.firstName) ?? '',
    lastName: approverAccess?.lastName ?? '',
    avatar: approverAccess?.avatar ? buildUrl(approverAccess.avatar) : undefined,
  }
}

export const buildMaterializedApprovers = (
  materializedStep: MaterializedStepType
): ApproverProps[] => {
  return materializedStep.stepApprovers?.map((approver) => {
    const strategy = approver.approvalStepApprover.strategy
    const accessId = approver.access?.id
    const newStatus = buildNewApproverStatus(approver.status)
    const accessInfo = normalizeApproverInfo(approver.access)
    return {
      id: approver.approvalStepApprover.id,
      status: approver.status, // ApprovalsMaterializedApprovalStepApproverApproverStatusEnum
      accessId: accessId,
      ...accessInfo,
      role: roleName(strategy),
      roleInitials: roleInitials(strategy),
      badge: approverBadge(approver.status),
      newStatus,
      updatedAt: approver.updatedAt,
      selectionCriteria: approver.selectionCriteria,
    }
  })
}

export const buildApprovers = (step: StepType): ApproverProps[] => {
  return step.approvers?.map((approver) => {
    const strategy = approver.strategy
    const accessId = approver.access?.id
    const accessInfo = normalizeApproverInfo(approver.access)
    return {
      id: approver.id,
      accessId: accessId,
      ...accessInfo,
      role: roleName(strategy),
      roleInitials: roleInitials(strategy),
      badge: approverBadge(status),
      status: null,
      updatedAt: null,
    }
  })
}
